import { AxiosResponse } from 'axios';

import { CommonModel } from '@telialt/models/src/CommonModel';

import { axiosForHybris } from '../axios/axiosForHybris';
import { getHybrisCSRFToken, getHybrisSegment, withParams } from '../helpers';
import QueryString from '../QueryString';
import { API } from './constants';
import {
    AuthStatusData,
    ConfirmPhoneNumberData,
    ConfirmUserData,
    CreatePasswordData,
    CSRFTokenResponse,
    LoginAuthenticationData,
    LoginConfirm2FAData,
    LoginConfirm2FAResponse,
    MSignAuthenticationData,
    PasetoTokenResponse,
    PasswordResetData,
    RegisterNewUserData,
    SmartIdAuthenticationData,
    TokenResponse,
    validateEmailTokenData,
} from './model';

const formConfig = {
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
    },
};

let smartIdStatusProgressiveTimeoutIndex: number = 0;
const smartIdStatusProgressiveTimeout: number[] = [5000, 10000, 15000, 120000];

const newAbortSignalSmartIdStatus = () => {
    const abortController = new AbortController();
    setTimeout(
        () => abortController.abort(),
        smartIdStatusProgressiveTimeout[smartIdStatusProgressiveTimeoutIndex] ||
            smartIdStatusProgressiveTimeout[smartIdStatusProgressiveTimeout.length - 1],
    );

    smartIdStatusProgressiveTimeoutIndex++;

    return abortController.signal;
};

export const authenticateLogin = (data: LoginAuthenticationData, segment: CommonModel.Segment, redirect?: string) =>
    axiosForHybris.post(withParams(API.URL_LOGIN_AUTH, { segment }), QueryString.stringify(data), {
        ...formConfig,
        params: { redirect },
    });

export const resetPassword = (data: PasswordResetData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_RESET_PASSWORD, { segment }), QueryString.stringify(data), formConfig);

export const createPassword = (data: CreatePasswordData) =>
    axiosForHybris.post(API.URL_CREATE_PASSWORD, QueryString.stringify(data), formConfig);

export const validateEmailToken = (data: validateEmailTokenData) =>
    axiosForHybris.post(API.URL_VALIDATE_TOKEN, QueryString.stringify(data), formConfig);

export const registerNewUser = (data: RegisterNewUserData, strong?: boolean) =>
    axiosForHybris.post(API.URL_REGISTER_USER, QueryString.stringify(data), {
        ...formConfig,
        params: { initiationSegment: getHybrisSegment(), strong },
    });

export const authenticateMSign = (data: MSignAuthenticationData, segment: CommonModel.Segment, redirect?: string) =>
    axiosForHybris.post(withParams(API.URL_MSIGN_AUTH, { segment }), QueryString.stringify(data), {
        ...formConfig,
        params: { redirect },
    });

export const checkMSignAuthStatus = (data: AuthStatusData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_MSIGN_AUTH_STATUS, { segment }), QueryString.stringify(data), formConfig);

export const cancelMSignAuth = (data: AuthStatusData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_MSIGN_AUTH_CANCEL, { segment }), QueryString.stringify(data), formConfig);

export const authenticateSmartId = (
    data: SmartIdAuthenticationData,
    segment: CommonModel.Segment,
    redirect?: string,
) => {
    smartIdStatusProgressiveTimeoutIndex = 0;

    return axiosForHybris.post(withParams(API.URL_SMART_ID_AUTH, { segment }), QueryString.stringify(data), {
        ...formConfig,
        params: { redirect },
    });
};
export const checkSmartIdAuthStatus = (data: AuthStatusData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_SMART_ID_AUTH_STATUS, { segment }), QueryString.stringify(data), {
        ...formConfig,
        signal: newAbortSignalSmartIdStatus(),
    });

export const cancelSmartIdAuth = (data: AuthStatusData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_SMART_ID_AUTH_CANCEL, { segment }), QueryString.stringify(data), formConfig);

export const loginConfirm2FA = (data: LoginConfirm2FAData, segment: CommonModel.Segment) =>
    axiosForHybris.post<LoginConfirm2FAResponse>(withParams(API.URL_LOGIN_CONFIRM_2FA, { segment }), data, {
        headers: {
            CSRFToken: getHybrisCSRFToken(),
        },
    });

export const resend2FACode = (segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_RESEND_2FA_CODE, { segment }), null, {
        headers: {
            CSRFToken: getHybrisCSRFToken(),
        },
    });

export const confirmPhoneNumber = (data: ConfirmPhoneNumberData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_CONFIRM_PHONE_NUMBER, { segment }), data, {
        headers: {
            CSRFToken: getHybrisCSRFToken(),
        },
    });

export const getCSRFToken = (): Promise<AxiosResponse<CSRFTokenResponse>> => axiosForHybris.get(API.URL_CSRF_TOKEN);

export const getToken = async (): Promise<AxiosResponse<TokenResponse>> => {
    let csrfToken: string = getHybrisCSRFToken();
    if (!csrfToken) {
        const tokenResponse = await getCSRFToken();
        csrfToken = tokenResponse.data?.csrfToken;
    }

    // Note: there is no difference if endpoint is called with b2b or b2c segment, same token is returned
    // hardcoding b2c segment to simplify usage
    return axiosForHybris.post<TokenResponse>(withParams(API.URL_AUTH_TOKEN, { segment: 'b2c' }), null, {
        headers: {
            CSRFToken: csrfToken,
        },
    });
};

export const confirmUserData = (data: ConfirmUserData, segment: CommonModel.Segment) =>
    axiosForHybris.post(withParams(API.URL_CONFIRM_USER_DATA, { segment }), QueryString.stringify(data), formConfig);

export const getPasetoToken = async (agreementId?: string): Promise<AxiosResponse<PasetoTokenResponse>> => {
    let csrfToken = getHybrisCSRFToken();
    if (!csrfToken) {
        const tokenResponse = await getCSRFToken();
        csrfToken = tokenResponse.data?.csrfToken;
    }

    const url = agreementId
        ? withParams(API.URL_PASETO_AUTH_TOKEN_B2B, { agreementId })
        : API.URL_PASETO_AUTH_TOKEN_B2C;

    return axiosForHybris.post<PasetoTokenResponse>(url, null, {
        headers: {
            CSRFToken: csrfToken,
        },
    });
};

export const getEshopPasetoToken = async (
    cartGuid: string,
    segment: CommonModel.Segment,
): Promise<AxiosResponse<PasetoTokenResponse>> => {
    let csrfToken = getHybrisCSRFToken();
    if (!csrfToken) {
        const tokenResponse = await getCSRFToken();
        csrfToken = tokenResponse.data?.csrfToken;
    }

    return axiosForHybris.post<PasetoTokenResponse>(
        withParams(API.URL_PASETO_AUTH_TOKEN_ANONYMOUS_ESHOP, { segment, cartGuid }),
        null,
        {
            headers: {
                CSRFToken: csrfToken,
            },
        },
    );
};
